import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, Trash2 } from 'lucide-react';
import { useNotifications, type Notification } from '../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';

function NotificationMessage({ notification }: { notification: Notification }) {
  const { type, data } = notification;

  const linkStyle = "font-medium text-accent1 hover:text-accent2";
  const textStyle = "text-input-text";

  // Early return if data is missing
  if (!data || typeof data !== 'object') {
    console.error('Invalid notification data:', notification);
    return null;
  }

  switch (type) {
    case 'idea_update':
      return (
        <span className={textStyle}>
          Status updated for{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>{' '}
          from {data.old_status} to {data.new_status}
        </span>
      );
    case 'new_idea':
      return (
        <span className={textStyle}>
          New book idea{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>{' '}
          has been posted
        </span>
      );
    case 'store_link':
      return (
        <span className={textStyle}>
          The book{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>{' '}
          is now available!{' '}
          <a
            href={data.store_link}
            target="_blank"
            rel="noopener noreferrer"
            className={linkStyle}
          >
            Get it here
          </a>
        </span>
      );
    case 'new_poll':
      return (
        <span className={textStyle}>
          New poll added to{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>
        </span>
      );
    case 'new_milestone':
      return (
        <span className={textStyle}>
          New milestone added to{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>
        </span>
      );
    case 'milestone_update':
      return (
        <span className={textStyle}>
          Milestone status in{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>{' '}
          updated from {data.old_status} to {data.status}
        </span>
      );
    case 'new_comment':
      return (
        <span className={textStyle}>
          <Link to={`/profile/${data.commenter_id}`} className={linkStyle}>
            {data.commenter_username}
          </Link>{' '}
          commented on{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>:{' '}
          <span className="italic">"{data.comment_preview}"</span>
        </span>
      );
    case 'poll_closed':
      return (
        <span className={textStyle}>
          Poll "{data.poll_title}" on{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>{' '}
          has ended with {data.total_votes} votes
        </span>
      );
    case 'new_follower':
      return (
        <span className={textStyle}>
          <Link to={`/profile/${data.follower_id}`} className={linkStyle}>
            {data.follower_username}
          </Link>{' '}
          started following you
        </span>
      );
    case 'new_idea_follower':
      return (
        <span className={textStyle}>
          <Link to={`/profile/${data.follower_id}`} className={linkStyle}>
            {data.follower_username}
          </Link>{' '}
          started following your idea{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>
        </span>
      );
    case 'new_assignment':
      return (
        <span className={textStyle}>
          New beta reading assignment for{' '}
          <Link to={`/beta/reader`} className={linkStyle}>
            {data.title}
          </Link>
          {data.due_date && (
            <span> (Due {new Date(data.due_date).toLocaleDateString()})</span>
          )}
        </span>
      );
    case 'assignment_started':
      return (
        <span className={textStyle}>
          <Link to={`/profile/${data.reader_id}`} className={linkStyle}>
            {data.reader_name}
          </Link>{' '}
          started reading{' '}
          <Link to={`/beta/author`} className={linkStyle}>
            {data.title}
          </Link>
        </span>
      );
    case 'assignment_completed':
      return (
        <span className={textStyle}>
          <Link to={`/profile/${data.reader_id}`} className={linkStyle}>
            {data.reader_name}
          </Link>{' '}
          completed{' '}
          <Link to={`/beta/author`} className={linkStyle}>
            {data.title}
          </Link>
        </span>
      );
    case 'new_assignment_feedback':
      return (
        <span className={textStyle}>
          <Link to={`/profile/${data.reader_id}`} className={linkStyle}>
            {data.reader_name}
          </Link>{' '}
          provided feedback on{' '}
          <Link to={`/beta/author`} className={linkStyle}>
            {data.title}
          </Link>:{' '}
          <span className="italic">"{data.feedback_preview}"</span>
        </span>
      );
    case 'beta_application_approved':
      return (
        <span className={textStyle}>
          Your beta reader application for{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>{' '}
          has been approved!
        </span>
      );
    case 'beta_application_rejected':
      return (
        <span className={textStyle}>
          Your beta reader application for{' '}
          <Link to={`/idea/${data.idea_id}`} className={linkStyle}>
            {data.idea_title}
          </Link>{' '}
          was not selected at this time
        </span>
      );
    case 'new_beta_application':
      return (
        <span className={textStyle}>
          New beta reader application from{' '}
          <Link to={`/profile/${data.reader_id}`} className={linkStyle}>
            {data.reader_name}
          </Link>{' '}
          for{' '}
          <Link to={`/beta/author`} className={linkStyle}>
            {data.idea_title}
          </Link>
        </span>
      );
    case 'test_notification':
      return (
        <span className={textStyle}>
          {data.message}
        </span>
      );
    default:
      console.warn('Unknown notification type:', type);
      return null;
  }
}

interface NotificationsDropdownProps {
  position?: 'top' | 'bottom';
}

export default function NotificationsDropdown({ position = 'bottom' }: NotificationsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, clearNotification } = useNotifications();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-accent1 hover:text-accent2 rounded-full hover:bg-accent3/10"
      >
        <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'fill-current' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={`absolute ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 w-96 bg-white rounded-lg shadow-lg border border-accent3 z-50`}>
          <div className="p-4 border-b border-accent3 flex items-center justify-between bg-white rounded-t-lg">
            <h3 className="font-semibold text-subheaders">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                className="text-sm text-accent1 hover:text-accent2 flex items-center gap-1"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all as read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto bg-white rounded-b-lg">
            {loading ? (
              <div className="p-4 text-center text-accent2">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-accent2">No notifications</div>
            ) : (
              <div className="divide-y divide-accent3">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 ${
                      !notification.read ? 'bg-accent3/10' : 'bg-white'
                    }`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="flex items-start gap-3">
                      {!notification.read && (
                        <span className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-accent1" />
                      )}
                      <div className="flex-1 min-w-0">
                        <NotificationMessage notification={notification} />
                        <p className="mt-1 text-sm text-accent2">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true
                          })}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          clearNotification(notification.id);
                        }}
                        className="p-1 text-accent2 hover:text-accent1"
                        title="Clear notification"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}